import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { neon } from "@/integrations/neon";
import { Plus, Trash2, Loader2, AlertTriangle, GripVertical } from "lucide-react";
import { getErrorMessage } from "@/utils/errors";

interface Question {
  id?: string;
  question_name: string;
  question_name_th: string;
  question_description: string;
  question_description_th: string;
  weight_percentage: number;
  poor_description: string;
  poor_description_th: string;
  excellent_description: string;
  excellent_description_th: string;
  order_index: number;
}

interface FormBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form?: {
    id: string;
    name: string;
    description: string | null;
    name_th: string | null;
    description_th: string | null;
  } | null;
  onFormSaved: () => void;
}

export const FormBuilder = ({ open, onOpenChange, form, onFormSaved }: FormBuilderProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formName, setFormName] = useState("");
  const [formNameTh, setFormNameTh] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formDescriptionTh, setFormDescriptionTh] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [weightError, setWeightError] = useState("");
  const { toast } = useToast();

  const resetForm = useCallback(() => {
    setFormName("");
    setFormNameTh("");
    setFormDescription("");
    setFormDescriptionTh("");
    setQuestions([]);
    setWeightError("");
  }, []);

  const loadFormData = useCallback(async () => {
    if (!form) {
      resetForm();
      return;
    }

    try {
      setFormName(form.name);
      setFormDescription(form.description || "");
      // Normalise Thai fields for existing forms
      setFormNameTh(form.name_th || "");
      setFormDescriptionTh(form.description_th || "");

      const formData = await neon.api.forms.get(form.id);

      const normalisedQuestions = (formData.questions || []).map((question) => ({
        ...question,
        question_name_th: question.question_name_th || "",
        question_description_th: question.question_description_th || "",
        poor_description: question.poor_description || "Poor",
        poor_description_th: question.poor_description_th || "",
        excellent_description: question.excellent_description || "Excellent",
        excellent_description_th: question.excellent_description_th || "",
      }));

      setQuestions(normalisedQuestions);
    } catch (error: unknown) {
      toast({
        title: "Error loading form",
        description: getErrorMessage(error, "Unable to load form"),
        variant: "destructive",
      });
    }
  }, [form, resetForm, toast]);

  useEffect(() => {
    if (open) {
      loadFormData();
    }
  }, [open, loadFormData]);

  const addQuestion = () => {
    const newQuestion: Question = {
      question_name: "",
      question_name_th: "",
      question_description: "",
      question_description_th: "",
      weight_percentage: 0,
      poor_description: "Poor",
      poor_description_th: "",
      excellent_description: "Excellent",
      excellent_description_th: "",
      order_index: questions.length,
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    // Update order indices
    updatedQuestions.forEach((q, i) => {
      q.order_index = i;
    });
    setQuestions(updatedQuestions);
    validateWeights(updatedQuestions);
  };

  const updateQuestion = (index: number, field: keyof Question, value: string | number) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setQuestions(updatedQuestions);

    if (field === "weight_percentage") {
      validateWeights(updatedQuestions);
    }
  };

  const validateWeights = (questionsToValidate: Question[]) => {
    const totalWeight = questionsToValidate.reduce((sum, q) => sum + Number(q.weight_percentage), 0);
    if (totalWeight !== 100 && questionsToValidate.length > 0) {
      setWeightError(`Total weight is ${totalWeight}%. Must equal 100%.`);
    } else {
      setWeightError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (questions.length === 0) {
      toast({
        title: "No questions added",
        description: "Please add at least one question to the form.",
        variant: "destructive",
      });
      return;
    }

    // Validate that all questions have names
    const emptyQuestions = questions.some((q) => !q.question_name.trim());
    if (emptyQuestions) {
      toast({
        title: "Empty questions",
        description: "Please provide names for all questions.",
        variant: "destructive",
      });
      return;
    }

    // Validate weights
    const totalWeight = questions.reduce((sum, q) => sum + Number(q.weight_percentage), 0);
    if (totalWeight !== 100) {
      toast({
        title: "Invalid weights",
        description: "Question weights must total exactly 100%.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      await neon.api.forms.save(
        {
          name: formName,
          name_th: formNameTh || null,
          description: formDescription || null,
          description_th: formDescriptionTh || null,
          questions,
        },
        form ? form.id : null
      );

      toast({
        title: form ? "Form updated" : "Form created",
        description: `The feedback form has been ${form ? "updated" : "created"} successfully.`,
      });

      onFormSaved();
    } catch (error: unknown) {
      toast({
        title: "Error saving form",
        description: getErrorMessage(error, "Failed to save form"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {form
              ? `Edit Form: ${form.name}${form.name_th ? ` / ${form.name_th}` : ""}`
              : "Create New Feedback Form"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Form Basic Info */}
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="formName">Form Name *</Label>
              <Input
                id="formName"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., General Script Feedback"
                required
              />
              <Label htmlFor="formNameTh" className="text-sm text-muted-foreground">
                Form Name (Thai)
              </Label>
              <Input
                id="formNameTh"
                value={formNameTh}
                onChange={(e) => setFormNameTh(e.target.value)}
                placeholder="เช่น แบบฟอร์มความคิดเห็นทั่วไป"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="formDescription">Form Description</Label>
              <Textarea
                id="formDescription"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Brief description of this feedback form"
                rows={2}
              />
              <Label htmlFor="formDescriptionTh" className="text-sm text-muted-foreground">
                Form Description (Thai)
              </Label>
              <Textarea
                id="formDescriptionTh"
                value={formDescriptionTh}
                onChange={(e) => setFormDescriptionTh(e.target.value)}
                placeholder="คำอธิบายแบบฟอร์ม (ภาษาไทย)"
                rows={2}
              />
            </div>
          </div>

          {/* Questions Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-base font-semibold">Questions</Label>
                <p className="text-sm text-muted-foreground">
                  Create questions for reviewers. Total weight must equal 100%.
                </p>
              </div>
              <Button type="button" onClick={addQuestion} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Question
              </Button>
            </div>

            {weightError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-destructive">{weightError}</span>
              </div>
            )}

            {questions.length === 0 ? (
              <Card className="text-center py-8 border-dashed">
                <CardContent>
                  <p className="text-muted-foreground">No questions added yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {questions.map((question, index) => (
                  <Card key={index} className="shadow-soft">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center space-x-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground" />
                          <span>Question {index + 1}</span>
                        </CardTitle>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(index)}
                          className="text-destructive hover:text-destructive-foreground hover:bg-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Question Name *</Label>
                          <Input
                            value={question.question_name}
                            onChange={(e) => updateQuestion(index, "question_name", e.target.value)}
                            placeholder="e.g., Character Development"
                            required
                          />
                          <Label className="text-sm text-muted-foreground">Question Name (Thai)</Label>
                          <Input
                            value={question.question_name_th}
                            onChange={(e) => updateQuestion(index, "question_name_th", e.target.value)}
                            placeholder="เช่น การพัฒนาตัวละคร"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Weight (%) *</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={question.weight_percentage}
                            onChange={(e) => updateQuestion(index, "weight_percentage", Number(e.target.value))}
                            placeholder="0"
                            required
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Question Description</Label>
                        <Textarea
                          value={question.question_description}
                          onChange={(e) => updateQuestion(index, "question_description", e.target.value)}
                          placeholder="Optional description to help reviewers understand what to evaluate"
                          rows={2}
                        />
                        <Label className="text-sm text-muted-foreground">Question Description (Thai)</Label>
                        <Textarea
                          value={question.question_description_th}
                          onChange={(e) => updateQuestion(index, "question_description_th", e.target.value)}
                          placeholder="คำอธิบายเพิ่มเติม (ภาษาไทย)"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Poor Description</Label>
                          <Input
                            value={question.poor_description}
                            onChange={(e) => updateQuestion(index, "poor_description", e.target.value)}
                            placeholder="Description for score 1"
                          />
                          <Label className="text-sm text-muted-foreground">Poor Description (Thai)</Label>
                          <Input
                            value={question.poor_description_th}
                            onChange={(e) => updateQuestion(index, "poor_description_th", e.target.value)}
                            placeholder="คำอธิบายคะแนน 1"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Excellent Description</Label>
                          <Input
                            value={question.excellent_description}
                            onChange={(e) => updateQuestion(index, "excellent_description", e.target.value)}
                            placeholder="Description for score 5"
                          />
                          <Label className="text-sm text-muted-foreground">Excellent Description (Thai)</Label>
                          <Input
                            value={question.excellent_description_th}
                            onChange={(e) => updateQuestion(index, "excellent_description_th", e.target.value)}
                            placeholder="คำอธิบายคะแนน 5"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || questions.length === 0 || !!weightError}
              className="bg-gradient-primary hover:opacity-90 transition-all duration-200"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {form ? "Update Form" : "Create Form"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
